import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Card, ConfigProvider, Descriptions, Flex } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import {
  isModuleEnabled,
  usePermissions,
} from 'business/contextProviders/usePermissions';
import { DefectReportHistory } from 'business/quality-control/pages/defect-report-history/components/defect-report-table';
import { Module_Enum } from 'generated/graphql';

export const DefectReportDetailsHeader = ({
  report,
  nextReport,
  previousReport,
  setSelectedReport,
}: {
  report: DefectReportHistory;
  nextReport: DefectReportHistory | undefined;
  previousReport: DefectReportHistory | undefined;
  setSelectedReport: (report: DefectReportHistory) => void;
}) => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { isDataManager } = usePermissions();

  const { t } = useTranslation();

  const items = [
    {
      key: 'ringNumber',
      label: t('qualityControl.defectReportHistory.ring'),
      children: report.ringNumber ?? '-',
    },
    {
      key: 'ringPosition',
      label: t('qualityControl.defectReportHistory.ringKey'),
      children: report.ringKey ? `P${report.ringKey}` : '-',
    },
    {
      key: 'segmentType',
      label: t('qualityControl.defectReportHistory.segmentType'),
      children: report.segmentType ?? '-',
    },
  ];

  const qualityScanModuleIsEnabled = isModuleEnabled(
    isDataManager,
    currentConstructionSite,
    Module_Enum.QualityScan,
  );
  if (qualityScanModuleIsEnabled) {
    items.push({
      key: 'serialNumber',
      label: t('qualityControl.defectReportHistory.serialNumber'),
      children: report.serialNumber ?? '-',
    });
  }

  return (
    <Flex gap={4} align="center">
      <Button
        type="text"
        icon={<LeftOutlined />}
        className="flex-1"
        disabled={!previousReport}
        onClick={() => previousReport && setSelectedReport(previousReport)}
      />
      <Card className="flex-15">
        <ConfigProvider
          theme={{
            components: {
              Descriptions: {
                itemPaddingBottom: 4,
                labelColor: '#595959',
              },
            },
          }}
        >
          <Descriptions
            items={items}
            colon={false}
            layout="vertical"
            column={qualityScanModuleIsEnabled ? 4 : 3}
          />
        </ConfigProvider>
      </Card>
      <Button
        type="text"
        icon={<RightOutlined />}
        className="flex-1"
        disabled={!nextReport}
        onClick={() => nextReport && setSelectedReport(nextReport)}
      />
    </Flex>
  );
};
